import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import './App.css';
import type { RootState, AppDispatch } from './redux/store';
import { fetchCurrentUser, logout } from './redux/slices/authSlice';
import { jwtDecode } from 'jwt-decode';
import toast from 'react-hot-toast';

import allRoutes from './routes';
import Loader from './components/Loader';
import AIChatWidget from './components/AIChatWidget';
import ErrorBoundary from './components/ErrorBoundary';
import UpgradeModal from './components/UpgradeModal';
import { AppContextProvider, useAppContext } from './contexts/AppContext';
import { UpgradeModalProvider } from './contexts/UpgradeModalContext';
import type { UpgradePayload } from './contexts/UpgradeModalContext';

const AppReadyGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { appReady } = useAppContext();
  const { isAuthenticated } = useSelector((state: RootState) => state.auth as any);

  if (isAuthenticated && !appReady) {
    return <Loader />;
  }
  return <>{children}</>;
};

const App: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth as any);
  const [authInitialized, setAuthInitialized] = useState(false);

  useEffect(() => {
    const sessionToken = localStorage.getItem('sessionToken');
    const isClientUser = !!localStorage.getItem('clientUser');
    if (sessionToken && !user && !isClientUser) {
      dispatch(fetchCurrentUser(sessionToken)).finally(() => setAuthInitialized(true));
    } else {
      setAuthInitialized(true);
    }
  }, [dispatch, user]);

  useEffect(() => {
    const checkTokenExpiration = () => {
      const token = localStorage.getItem('token');
      if (token) {
        const isClientUser = !!localStorage.getItem('clientUser');
        if (isClientUser) return;

        try {
          const decoded: any = jwtDecode(token);
          if (decoded.exp * 1000 < Date.now()) {
            if (!window.__sessionExpiredShown) {
              window.__sessionExpiredShown = true;
              toast.error('Session expired. Please login again.');
              dispatch(logout());
              window.location.href = '/login';
            }
          }
        } catch (e) {
          dispatch(logout());
          window.location.href = '/login';
        }
      }
    };

    checkTokenExpiration();

    const interval = setInterval(checkTokenExpiration, 60000);
    window.addEventListener('focus', checkTokenExpiration);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', checkTokenExpiration);
    };
  }, [dispatch]);

  if (!authInitialized) {
    return <Loader />;
  }

  return (
    <Router>
      <AppContextProvider>
        <UpgradeModalProvider
          renderModal={(open, payload, onClose) => (
            <UpgradeModal open={open} payload={payload} onClose={onClose} />
          )}
        >
          <AppReadyGate>
            <ErrorBoundary>
              <Routes>
                {allRoutes}
              </Routes>
            </ErrorBoundary>
          </AppReadyGate>

          <Toaster
            position="top-center"
            reverseOrder={false}
            toastOptions={{
              duration: 3000,
            }}
          />
        </UpgradeModalProvider>
      </AppContextProvider>
    </Router>
  );
};

export default App;